import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Tooltip, Divider
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import {
  getDeductionTemplates, createDeductionTemplate, updateDeductionTemplate, deleteDeductionTemplate
} from '../api';

const CATEGORIES = [
  { value: 'health', label: 'Health Insurance' },
  { value: 'dental', label: 'Dental Insurance' },
  { value: 'vision', label: 'Vision Insurance' },
  { value: '401k', label: '401(k)' },
  { value: 'roth_401k', label: 'Roth 401(k)' },
  { value: 'hsa', label: 'HSA' },
  { value: 'fsa', label: 'FSA' },
  { value: 'life_insurance', label: 'Life Insurance' },
  { value: 'disability', label: 'Disability Insurance' },
  { value: 'union_dues', label: 'Union Dues' },
  { value: 'garnishment', label: 'Garnishment' },
  { value: 'other', label: 'Other' },
];

const TAX_TREATMENTS = [
  { value: 'pre_tax', label: 'Pre-Tax' },
  { value: 'post_tax', label: 'Post-Tax' },
];

const CALC_METHODS = [
  { value: 'flat_amount', label: 'Flat Amount ($)' },
  { value: 'percentage', label: 'Percentage (%)' },
];

const EMPLOYER_CALC_METHODS = [
  { value: 'flat_amount', label: 'Flat Amount ($)' },
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'match', label: 'Match Employee (%)' },
];

const EMPTY_FORM = {
  name: '', category: 'health', tax_treatment: 'pre_tax',
  calc_method: 'flat_amount', default_amount: '', is_active: true,
  employer_amount: 0, employer_calc_method: 'flat_amount',
  employer_employee_amount: 0, employer_dependent_amount: 0,
};

export default function DeductionTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getDeductionTemplates(false);
      setTemplates(res.data);
    } catch (err) {
      setError('Failed to load deduction templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditing(template);
      setForm({
        name: template.name,
        category: template.category,
        tax_treatment: template.tax_treatment,
        calc_method: template.calc_method,
        default_amount: template.default_amount,
        is_active: template.is_active,
        employer_amount: template.employer_amount || 0,
        employer_calc_method: template.employer_calc_method || 'flat_amount',
        employer_employee_amount: template.employer_employee_amount || 0,
        employer_dependent_amount: template.employer_dependent_amount || 0,
      });
    } else {
      setEditing(null);
      setForm(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const data = {
        ...form,
        default_amount: parseFloat(form.default_amount) || 0,
        employer_amount: parseFloat(form.employer_amount) || 0,
        employer_employee_amount: parseFloat(form.employer_employee_amount) || 0,
        employer_dependent_amount: parseFloat(form.employer_dependent_amount) || 0,
      };
      if (editing) {
        await updateDeductionTemplate(editing.id, data);
        setSuccess('Deduction template updated');
      } else {
        await createDeductionTemplate(data);
        setSuccess('Deduction template created');
      }
      setDialogOpen(false);
      loadTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save deduction template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDeductionTemplate(id);
      setSuccess('Deduction template deactivated');
      setConfirmDelete(null);
      loadTemplates();
    } catch (err) {
      setError('Failed to deactivate deduction template');
    }
  };

  const getCategoryLabel = (val) => CATEGORIES.find(c => c.value === val)?.label || val;

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    {
      field: 'category', headerName: 'Category', width: 160,
      renderCell: ({ value }) => getCategoryLabel(value)
    },
    {
      field: 'tax_treatment', headerName: 'Tax Treatment', width: 120,
      renderCell: ({ value }) => (
        <Chip
          label={value === 'pre_tax' ? 'Pre-Tax' : 'Post-Tax'}
          size="small"
          color={value === 'pre_tax' ? 'info' : 'default'}
        />
      )
    },
    {
      field: 'calc_method', headerName: 'Method', width: 130,
      renderCell: ({ value }) => value === 'flat_amount' ? 'Flat ($)' : 'Percentage (%)'
    },
    {
      field: 'default_amount', headerName: 'Default', width: 110,
      renderCell: ({ row }) => row.calc_method === 'percentage'
        ? `${row.default_amount}%`
        : `$${parseFloat(row.default_amount).toFixed(2)}`
    },
    {
      field: 'employer_amount', headerName: 'Employer', width: 130,
      renderCell: ({ row }) => {
        const amt = parseFloat(row.employer_amount || 0);
        if (amt === 0) return '—';
        if (row.employer_calc_method === 'percentage') return `${amt}%`;
        if (row.employer_calc_method === 'match') return `${amt}% (match)`;
        return `$${amt.toFixed(2)}`;
      }
    },
    {
      field: 'is_active', headerName: 'Status', width: 100,
      renderCell: ({ value }) => (
        <Chip label={value ? 'Active' : 'Inactive'} size="small" color={value ? 'success' : 'default'} />
      )
    },
    {
      field: 'actions', headerName: 'Actions', width: 120, sortable: false,
      renderCell: ({ row }) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleOpenDialog(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.is_active && (
            <Tooltip title="Deactivate">
              <IconButton size="small" color="error" onClick={() => setConfirmDelete(row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    },
  ];

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Deduction Templates</Typography>
          <Box>
            <Button startIcon={<Refresh />} onClick={loadTemplates} sx={{ mr: 1 }}>Refresh</Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Template
            </Button>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define reusable deduction types (health insurance, 401k, etc.) that can be assigned to employees for automatic payroll calculation.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <DataGrid
          rows={templates}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50]}
          sx={{ '& .MuiDataGrid-cell': { py: 1 } }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Deduction Template' : 'Add Deduction Template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name" required fullWidth
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Health Insurance PPO"
            />
            <TextField
              select label="Category" fullWidth
              value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            >
              {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField
              select label="Tax Treatment" fullWidth
              value={form.tax_treatment} onChange={e => setForm(f => ({ ...f, tax_treatment: e.target.value }))}
            >
              {TAX_TREATMENTS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField
              select label="Calculation Method" fullWidth
              value={form.calc_method} onChange={e => setForm(f => ({ ...f, calc_method: e.target.value }))}
            >
              {CALC_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField
              label={form.calc_method === 'percentage' ? 'Default Percentage (%)' : 'Default Amount ($)'}
              type="number" fullWidth
              value={form.default_amount}
              onChange={e => setForm(f => ({ ...f, default_amount: e.target.value }))}
              inputProps={{ min: 0, step: form.calc_method === 'percentage' ? 0.1 : 0.01 }}
            />
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">Employer Contribution</Typography>
            <TextField
              select label="Employer Calc Method" fullWidth
              value={form.employer_calc_method}
              onChange={e => setForm(f => ({ ...f, employer_calc_method: e.target.value }))}
            >
              {EMPLOYER_CALC_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField
              label={form.employer_calc_method === 'flat_amount' ? 'Employer Amount ($)' : 'Employer Percentage (%)'}
              type="number" fullWidth
              value={form.employer_amount}
              onChange={e => setForm(f => ({ ...f, employer_amount: e.target.value }))}
              inputProps={{ min: 0, step: form.employer_calc_method === 'flat_amount' ? 0.01 : 0.1 }}
              helperText={form.employer_calc_method === 'match' ? 'Matches employee contribution up to this % of gross' : ''}
            />
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Employer Contribution Split (optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Split employer contribution into employee and dependent portions (like Gusto). Leave at 0 to use the single employer amount above.
            </Typography>
            <TextField
              label="Employee Portion ($)" type="number" fullWidth
              value={form.employer_employee_amount}
              onChange={e => setForm(f => ({ ...f, employer_employee_amount: e.target.value }))}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Employer contribution for the employee's own coverage"
            />
            <TextField
              label="Dependent Portion ($)" type="number" fullWidth
              value={form.employer_dependent_amount}
              onChange={e => setForm(f => ({ ...f, employer_dependent_amount: e.target.value }))}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Employer contribution for dependent coverage"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (editing ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Deactivate Template?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate "{confirmDelete?.name}"? Existing employee deductions using this template will remain active.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDelete.id)}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
